// pageInside/payments/WalletBalancePanel.jsx
//
// "My Wallet": every amount is computed by the server from the earnings
// ledger (verified captures only) -- the browser never builds a balance.
// Pending (no delivery yet / 7-day window), blocked (return, refund,
// dispute), available, withdrawals in progress and withdrawn are separate.
import React, { useCallback, useEffect, useState } from "react";
import { elakayAutoText } from "../dataElakay";
import { getWalletBalanceAPI, getWalletEarningsAPI, getWalletWithdrawalsAPI, refreshWithdrawalAPI, getWithdrawalBankAccountsAPI } from "../../auth/paymentsApi";
import { FaUniversity } from "react-icons/fa";
import { formatCents, withdrawalMethodState } from "./paymentMethodsLogic";
import { blockReasonLabel, earningStateLabel, paymentErrorMessage, withdrawalStatusLabel } from "./walletLabels";
import WithdrawMoneyModal from "./WithdrawMoneyModal";
import PayPalRecipientCard from "./PayPalRecipientCard";
import "./payments.css";

const t = elakayAutoText;
const date = (value) => (value ? new Date(value).toLocaleDateString() : "—");
const dateTime = (value) => (value ? new Date(value).toLocaleString() : "—");

const Card = ({ label, cents, sub, primary }) => (
  <div className={`elk-wallet__card${primary ? " elk-wallet__card--primary" : ""}`}>
    <div className="elk-wallet__label">{label}</div>
    <div className="elk-wallet__value">{formatCents(cents)}</div>
    {sub && <div className="elk-wallet__sub">{sub}</div>}
  </div>
);

const EARNING_FILTERS = ["", "pending_delivery", "waiting_release", "available", "blocked", "withdrawn"];

// Banking -> bank accounts of the user's OWN Stripe Connect account (masked).
// Adding / editing banks stays in the existing Stripe banking section below.
const BankAccountsSummary = () => {
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    getWithdrawalBankAccountsAPI().then((data) => setState({ loading: false, data })).catch(() => setState({ loading: false, data: null }));
  }, []);
  const d = state.data;
  return (
    <div className="elk-ps__group" style={{ marginTop: 12 }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}><FaUniversity aria-hidden="true" />{t("h_pay_bank_accounts", "Bank accounts")}</h3>
      {state.loading ? <div className="elk-pay-hint">{t("h_pay_loading", "Loading...")}</div> : !d?.connected ? (
        <div className="elk-pay-hint">{t("h_pay_bank_add_hint", "Create your payout account and add a bank account in the banking section below.")}</div>
      ) : (
        <>
          {!d.payoutsEnabled && <div className="elk-notice elk-notice--warn">{t("h_pay_account_not_verified", "Your payout account is not verified yet. Complete the Stripe verification first.")}</div>}
          {d.banks.length === 0 ? <div className="elk-pay-hint">{t("h_pay_bank_required", "Add and select a bank account first.")}</div> : d.banks.map((b) => (
            <div className="elk-summary-row" key={b.id}>
              <span>{b.bankName || t("h_pay_bank_account", "Bank Account")} •••• {b.last4} ({String(b.currency).toUpperCase()}, {b.country})</span>
              <strong>{b.isDefault ? t("h_pay_default_destination", "Default withdrawal destination") : ""}</strong>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const WalletBalancePanel = ({ openSignal = 0 }) => {
  const [balance, setBalance] = useState(null);
  const [methods, setMethods] = useState({ bankTransfer: false, paypal: false });
  const [methodStatus, setMethodStatus] = useState({});
  const [error, setError] = useState("");
  const [tab, setTab] = useState("earnings");
  const [earnings, setEarnings] = useState({ loading: true, items: [], page: 1, pages: 1, state: "" });
  const [wds, setWds] = useState({ loading: true, items: [] });
  const [showWithdraw, setShowWithdraw] = useState(false);

  const loadBalance = useCallback(async () => {
    try {
      const res = await getWalletBalanceAPI();
      setBalance(res.balance);
      setMethods(res.withdrawalMethods || {});
      setMethodStatus(res.withdrawalStatus || {});
      setError("");
    } catch (e) {
      setError(paymentErrorMessage(e));
    }
  }, []);
  const loadEarnings = useCallback(async (page = 1, state = "") => {
    setEarnings((s) => ({ ...s, loading: true }));
    try {
      const res = await getWalletEarningsAPI({ page, limit: 20, state });
      setEarnings({ loading: false, items: res.items || [], page: res.page, pages: res.pages, state });
    } catch (e) {
      setEarnings((s) => ({ ...s, loading: false }));
      setError(paymentErrorMessage(e));
    }
  }, []);
  const loadWithdrawals = useCallback(async () => {
    try {
      const res = await getWalletWithdrawalsAPI({ limit: 30 });
      setWds({ loading: false, items: res.items || [] });
    } catch (e) {
      setWds({ loading: false, items: [] });
    }
  }, []);

  useEffect(() => { loadBalance(); loadEarnings(1, ""); loadWithdrawals(); }, [loadBalance, loadEarnings, loadWithdrawals]);
  useEffect(() => { if (openSignal > 0) setShowWithdraw(true); }, [openSignal]);

  const refreshOne = async (id) => {
    try { await refreshWithdrawalAPI(id); } catch { /* status unchanged */ }
    loadWithdrawals(); loadBalance();
  };

  const methodState = withdrawalMethodState(methods);
  const availableCents = balance?.availableCents || 0;

  return (
    <section className="elk-wallet" aria-labelledby="elk-wallet-title">
      <h2 id="elk-wallet-title" className="elk-section-title" style={{ fontSize: "1.2rem" }}>{t("h_pay_my_wallet", "My Wallet")}</h2>
      {error && <div className="elk-notice elk-notice--error" role="alert">{error}</div>}
      {!balance ? <div className="elk-pay-hint">{t("h_pay_loading", "Loading...")}</div> : (
        <>
          <div className="elk-wallet__grid">
            <Card label={t("h_pay_available_balance", "Available Balance")} cents={availableCents} primary
              sub={balance.negativeBalanceCents > 0 ? `${t("h_pay_negative_balance", "Refund to recover")}: ${formatCents(balance.negativeBalanceCents)}` : null} />
            <Card label={t("h_pay_pending_earnings", "Pending Earnings")} cents={balance.pendingCents}
              sub={`${t("h_pay_waiting_delivery", "Waiting for Delivery")}: ${formatCents(balance.pendingDeliveryCents)} · ${t("h_pay_waiting_release", "Waiting for Release")}: ${formatCents(balance.waitingReleaseCents)}${balance.nextReleaseAt ? ` · ${t("h_pay_next_release", "Next release")}: ${dateTime(balance.nextReleaseAt)}` : ""}`} />
            <Card label={t("h_pay_blocked_earnings", "Blocked Earnings")} cents={balance.blockedCents} />
            <Card label={t("h_pay_withdrawal_in_progress", "Withdrawal in Progress")} cents={balance.withdrawalsInProgressCents} />
            <Card label={t("h_pay_total_earnings", "Total earnings")} cents={balance.totalEarningsCents} sub={`${t("h_pay_withdrawn", "Withdrawn")}: ${formatCents(balance.withdrawnCents + balance.legacyTransferredCents)}`} />
          </div>
          <div className="elk-wallet__actions">
            <button type="button" className="elk-btn elk-btn--primary" disabled={methodState.unavailable || availableCents <= 0} onClick={() => setShowWithdraw(true)}>
              {t("h_pay_withdraw_money", "Withdraw Money")}
            </button>
            {methodState.unavailable && <span className="elk-pay-hint elk-pay-hint--warn">{t("h_pay_withdrawals_unavailable", "Withdrawals are temporarily unavailable. Your balance and history remain available.")}</span>}
            {!methodState.unavailable && availableCents <= 0 && <span className="elk-pay-hint">{t("h_pay_release_rule", "Product earnings become available 7 full days after confirmed delivery, if no return or dispute is open.")}</span>}
          </div>
        </>
      )}

      <div className="elk-banking-head">{t("h_pay_banking", "Banking")}</div>
      <BankAccountsSummary />
      <PayPalRecipientCard enabled={methodStatus.paypal?.enabled === true} operational={methods.paypal === true} />

      <div className="elk-tabs" role="tablist" style={{ marginTop: 16 }}>
        <button type="button" role="tab" aria-selected={tab === "earnings"} className={`elk-pay-methods__option${tab === "earnings" ? " is-active" : ""}`} onClick={() => setTab("earnings")}>{t("h_pay_earnings_history", "Earnings history")}</button>
        <button type="button" role="tab" aria-selected={tab === "withdrawals"} className={`elk-pay-methods__option${tab === "withdrawals" ? " is-active" : ""}`} onClick={() => setTab("withdrawals")}>{t("h_pay_withdrawal_history", "Withdrawal history")}</button>
      </div>

      {tab === "earnings" ? (
        <>
          <div className="elk-field" style={{ maxWidth: 260 }}>
            <label htmlFor="elk-earn-filter">{t("h_pay_filter", "Filter")}</label>
            <select id="elk-earn-filter" value={earnings.state} onChange={(e) => loadEarnings(1, e.target.value)}>
              {EARNING_FILTERS.map((s) => <option key={s || "all"} value={s}>{s ? earningStateLabel(s) : t("h_pay_all", "All")}</option>)}
            </select>
          </div>
          <div className="elk-table-wrap">
            <table className="elk-table">
              <thead>
                <tr>
                  <th>{t("h_pay_transaction_id", "Transaction ID")}</th>
                  <th>{t("h_pay_order", "Order")}</th>
                  <th>{t("h_pay_type", "Type")}</th>
                  <th className="num">{t("h_pay_gross", "Gross")}</th>
                  <th className="num">{t("h_pay_refunds_fees", "Refunds / fees")}</th>
                  <th className="num">{t("h_pay_net_amount", "Net amount")}</th>
                  <th>{t("h_pay_payment_date", "Payment date")}</th>
                  <th>{t("h_pay_delivery_date", "Delivery date")}</th>
                  <th>{t("h_pay_release_eligibility", "Release eligibility")}</th>
                  <th>{t("h_pay_status", "Status")}</th>
                </tr>
              </thead>
              <tbody>
                {earnings.loading ? (
                  <tr><td colSpan={10}>{t("h_pay_loading", "Loading...")}</td></tr>
                ) : earnings.items.length === 0 ? (
                  <tr><td colSpan={10}>{t("h_pay_no_earnings", "No earnings yet.")}</td></tr>
                ) : earnings.items.map((e) => (
                  <tr key={e.id}>
                    <td title={e.id}>{e.id.slice(-8)}</td>
                    <td>{e.orderRef}{e.productName ? <div className="elk-ps__meta">{e.productName}</div> : null}</td>
                    <td>{e.beneficiaryType}</td>
                    <td className="num">{formatCents(e.grossCents)}</td>
                    <td className="num">{e.adjustmentsCents ? `−${formatCents(e.adjustmentsCents)}` : "—"}</td>
                    <td className="num">{formatCents(e.netCents)}</td>
                    <td>{date(e.paidAt)}</td>
                    <td>{date(e.deliveredAt)}</td>
                    <td>{e.releaseEligibleAt ? dateTime(e.releaseEligibleAt) : "—"}</td>
                    <td>
                      <span className={`elk-badge elk-badge--${e.state}`}>{earningStateLabel(e.state)}</span>
                      {e.blockReason && <div className="elk-ps__meta">{blockReasonLabel(e.blockReason)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {earnings.pages > 1 && (
            <div className="elk-wallet__actions" style={{ marginTop: 8 }}>
              <button type="button" className="elk-btn elk-btn--ghost" disabled={earnings.page <= 1} onClick={() => loadEarnings(earnings.page - 1, earnings.state)}>‹</button>
              <span className="elk-pay-hint">{earnings.page} / {earnings.pages}</span>
              <button type="button" className="elk-btn elk-btn--ghost" disabled={earnings.page >= earnings.pages} onClick={() => loadEarnings(earnings.page + 1, earnings.state)}>›</button>
            </div>
          )}
        </>
      ) : (
        <div className="elk-table-wrap">
          <table className="elk-table">
            <thead>
              <tr>
                <th>{t("h_pay_date", "Date")}</th>
                <th>{t("h_pay_destination", "Destination")}</th>
                <th className="num">{t("h_pay_amount", "Amount")}</th>
                <th>{t("h_pay_status", "Status")}</th>
                <th>{t("h_pay_reference", "Reference")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {wds.loading ? <tr><td colSpan={6}>{t("h_pay_loading", "Loading...")}</td></tr> : wds.items.length === 0 ? (
                <tr><td colSpan={6}>{t("h_pay_no_withdrawals", "No withdrawals yet.")}</td></tr>
              ) : wds.items.map((w) => (
                <tr key={w.id}>
                  <td>{dateTime(w.requestedAt)}</td>
                  <td>{w.method === "paypal" ? `PayPal ${w.destination.maskedEmail}` : `${w.destination.bankName || t("h_pay_bank_account", "Bank Account")} •••• ${w.destination.last4}`}</td>
                  <td className="num">{formatCents(w.amountCents)}</td>
                  <td><span className={`elk-badge elk-badge--${w.status}`}>{withdrawalStatusLabel(w.status)}</span>{w.failureMessage && <div className="elk-ps__meta">{w.failureMessage}</div>}</td>
                  <td>{w.providerReference ? w.providerReference.slice(-10) : "—"}</td>
                  <td>{["pending", "processing"].includes(w.status) && <button type="button" className="elk-btn elk-btn--ghost" onClick={() => refreshOne(w.id)}>{t("h_pay_refresh", "Refresh")}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <WithdrawMoneyModal
        show={showWithdraw}
        onHide={() => setShowWithdraw(false)}
        availableCents={availableCents}
        withdrawalMethods={methods}
        onDone={() => { loadBalance(); loadWithdrawals(); loadEarnings(1, earnings.state); }}
      />
    </section>
  );
};

export default WalletBalancePanel;
